import type { Building } from "building-types/building/buildingTypes";
import { getSourceRoomId } from "building-types/components/componentInstanceUtils";
import { FloorFinishType, type FloorAssemblyFinishLayerOverride } from "building-types/floors/floorFinishTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import type { Room } from "building-types/rooms/roomTypes";
import type { RWTable } from "common/data/table";
import { docEntityGetSortedChildrenFromTable } from "doc/entity/docEntityGetChildrenFromTable";
import { evalFormulas, evaluateEntityForComponentInstance, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { polyLine2Copy, polyLine2Move, polyLine2ReflectOverYAxis } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { applyTransformToOutline } from "../utils/componentTransforms";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  optionChoices,
  entityDisabledByGroup,
  rooms,
  mirror,
  mirrorTranslationX,
  doc: { floorFinishes, optionSets, instanceOptionSets },
}: Building) {
  const definitions = { optionSets, instanceOptionSets };
  return {
    rooms,
    floorFinishes,
    optionChoices: resolveOptionsInFormula({ floorFinishes }, definitions, optionChoices),
    rawOptionChoices: optionChoices,
    definitions,
    entityDisabledByGroup,
    mirror,
    mirrorTranslationX,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  definitions: "shallow",
  floorFinishes: "deep",
  optionChoices: "shallow",
});

function applyComponentTransformToPerimeter(finishPerimeter: PolyLine2, room: Room): PolyLine2 {
  const { componentTransform } = room;
  if (componentTransform) {
    return applyTransformToOutline(componentTransform.matrix, finishPerimeter);
  }
  return finishPerimeter;
}

function generate({
  rooms,
  floorFinishes: rawFloorFinishes,
  optionChoices,
  rawOptionChoices,
  definitions,
  entityDisabledByGroup,
  mirror,
  mirrorTranslationX,
}: ReturnType<typeof select>): {
  roomFloorFinishes: Building["roomFloorFinishes"];
} {
  const roomFloorFinishes: RWTable<FloorAssemblyFinishLayerOverride[]> = {};

  if (!rooms) {
    return { roomFloorFinishes };
  }

  const archetypeFloorFinishes = evalFormulas(
    rawFloorFinishes,
    (v) => optionChoices[v],
    undefined,
    entityDisabledByGroup,
  );

  for (const roomId in rooms) {
    const room = rooms[roomId];

    const baseRoomId = getSourceRoomId(room);
    const componentInstanceChain = room.componentTransform?.componentInstanceChain;

    roomFloorFinishes[room.id] = docEntityGetSortedChildrenFromTable(rawFloorFinishes, baseRoomId)
      .map((rawFinish) =>
        evaluateFloorFinishForRoom(
          rawFinish,
          archetypeFloorFinishes[rawFinish.id],
          componentInstanceChain,
          definitions,
          rawOptionChoices,
        ),
      )
      .filter((finish) => !finish.disabled)
      .map((finish) => {
        const { finishPerimeter, finishType } = finish;
        if (finishType !== FloorFinishType.DYNAMIC && finishPerimeter) {
          let transformedPerimeter = applyComponentTransformToPerimeter(finishPerimeter, room);
          if (mirror) {
            transformedPerimeter = polyLine2ReflectOverYAxis(polyLine2Copy(transformedPerimeter));
            polyLine2Move(transformedPerimeter, mirrorTranslationX, 0);
          }
          return { ...finish, finishPerimeter: transformedPerimeter };
        }
        return finish;
      });
  }

  return { roomFloorFinishes };
}

export default memo(select, generate, compare);

/**
 * Evaluate a floor finish override using component-instance-aware option
 * resolution when the room belongs to a component instance, falling back to
 * the archetype-evaluated entity (which already had group disabling applied)
 * for non-component rooms.
 */
function evaluateFloorFinishForRoom(
  rawFinish: FloorAssemblyFinishLayerOverride,
  archetypeFinish: FloorAssemblyFinishLayerOverride,
  componentInstanceChain: readonly string[] | undefined,
  definitions: OptionDefinitions,
  rawOptionChoices: OptionChoices,
): FloorAssemblyFinishLayerOverride {
  if (componentInstanceChain && componentInstanceChain.length > 0) {
    return evaluateEntityForComponentInstance(rawFinish, componentInstanceChain, definitions, rawOptionChoices);
  }
  return archetypeFinish;
}
